import { Button, Flex } from "antd";
import {
  AlignBottomIcon,
  AlignCenterHorizontalIcon,
  AlignCenterVerticalIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
} from "components/icons";
import { useTranslate } from "@refinedev/core";
import { ReactNode } from "react";
import { useAntTheme } from "hooks/useAntTheme/";
import { ItemControlsConfigProvider } from "../../../controls/ItemControlsConfigProvider";
import { BrandKitAsset, PaletteResponse } from "pages/media/types";
import {
  HorizontalTextAlign,
  TableTextStyle,
  VerticalTextAlign,
} from "../../../../types";
import { GroupedFontPicker } from "pages/media/components/GroupedFontPicker/GroupedFontPicker";
import { IncreaseDecreaseNumberInput } from "../../../controls/IncreaseDecreaseNumberInput";
import { TextUppercaseSettings } from "../../../controls/TextUppercaseSettings";
import { IconColorPicker } from "../../../controls/IconColorPicker";
import { ButtonsWrapper } from "../../ButtonWrapper";
import {
  resolveFontAssetIdToBrandKitPlaceholder,
  resolveToBrandkitFontIfNeeded,
} from "../../../../helpers/itemsHelper";

const MIN_FONT_SIZE = 5;

export const TableTextStyleFeature = ({
  textStyle,
  palette,
  organizationId,
  brandKitFonts,
  titleFont,
  textFont,
  subtitlesFont,
  onChange,
}: {
  textStyle: TableTextStyle;
  palette?: PaletteResponse;
  organizationId?: string;
  brandKitFonts: BrandKitAsset[];
  titleFont?: BrandKitAsset;
  textFont?: BrandKitAsset;
  subtitlesFont?: BrandKitAsset;
  onChange: (style: Partial<TableTextStyle>) => void;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const resolvedFontAssetId = resolveToBrandkitFontIfNeeded(
    textStyle.fontAssetId,
    titleFont,
    subtitlesFont,
    textFont
  );

  const currentVerticalAlign: VerticalTextAlign =
    textStyle.verticalAlign ?? "top";

  const horizontalAlignButtons: {
    key: HorizontalTextAlign;
    icon: ReactNode;
  }[] = [
    { key: "left", icon: <AlignLeftIcon /> },
    { key: "center", icon: <AlignCenterHorizontalIcon /> },
    { key: "right", icon: <AlignRightIcon /> },
  ];

  const verticalAlignButtons: {
    key: VerticalTextAlign;
    icon: ReactNode;
  }[] = [
    { key: "top", icon: <AlignTopIcon /> },
    { key: "center", icon: <AlignCenterVerticalIcon /> },
    { key: "bottom", icon: <AlignBottomIcon /> },
  ];

  return (
    <ItemControlsConfigProvider>
      <Flex vertical gap={12}>
        <Flex align="center" gap={8}>
          <GroupedFontPicker
            value={resolvedFontAssetId}
            organizationId={organizationId}
            brandKitFonts={brandKitFonts}
            onChange={(fontPath, fontAssetId) => {
              const resolvedId = resolveFontAssetIdToBrandKitPlaceholder(
                fontAssetId,
                brandKitFonts
              );
              onChange({
                fontAssetId: resolvedId,
                fontPath,
              });
            }}
          />
          <IncreaseDecreaseNumberInput
            value={textStyle.fontSize}
            min={MIN_FONT_SIZE}
            onChange={(value) => {
              if (typeof value === "number") {
                onChange({ fontSize: value });
              }
            }}
          />
        </Flex>
        <Flex align="center" gap={8}>
          {palette && (
            <>
              <IconColorPicker
                value={textStyle.color}
                allowClear={false}
                disabledAlpha={true}
                onChangeComplete={(resolvedColor) => {
                  onChange({ color: resolvedColor as string });
                }}
                title={t("components.templates.settings.textColor")}
                palette={palette}
                type="text"
                hint={t(
                  "components.templates.settings.TextControls.iconColorPicker.text"
                )}
              />
              <IconColorPicker
                value={textStyle.strokeColor}
                allowClear
                disabledAlpha={true}
                onClear={() => onChange({ strokeColor: undefined })}
                onChangeComplete={(resolvedColor) => {
                  onChange({
                    strokeColor: resolvedColor as string,
                  });
                }}
                title={t("components.templates.settings.strokeColor")}
                palette={palette}
                type="stroke"
                hint={t(
                  "components.templates.settings.TextControls.iconColorPicker.stroke"
                )}
              />
            </>
          )}
          <TextUppercaseSettings
            value={textStyle.upperCase}
            onChange={(isUpperCase) => {
              onChange({ upperCase: isUpperCase });
            }}
          />
        </Flex>
        <Flex gap={12} vertical align="start">
          <ButtonsWrapper $hasBackground>
            {horizontalAlignButtons.map((button) => (
              <Button
                data-testid="text-alignment-button"
                key={button.key}
                icon={button.icon}
                type="text"
                onClick={() => onChange({ textAlign: button.key })}
                style={{
                  height: 24,
                  width: 32,
                  backgroundColor:
                    textStyle.textAlign === button.key
                      ? theme.colorBgBase
                      : undefined,
                }}
              />
            ))}
          </ButtonsWrapper>
          <ButtonsWrapper $hasBackground>
            {verticalAlignButtons.map((button) => (
              <Button
                data-testid="text-alignment-button"
                key={button.key}
                icon={button.icon}
                type="text"
                onClick={() => onChange({ verticalAlign: button.key })}
                style={{
                  height: 24,
                  width: 32,
                  backgroundColor:
                    currentVerticalAlign === button.key
                      ? theme.colorBgBase
                      : undefined,
                }}
              />
            ))}
          </ButtonsWrapper>
        </Flex>
      </Flex>
    </ItemControlsConfigProvider>
  );
};
